import React, { useEffect, useRef, useState, useCallback } from "react";

import { initFlowbite } from "flowbite";
import { MessageCircle } from "lucide-react";
import { QrCode as QrCodeIcon } from "lucide-react";
import { useCustomSelectors } from "../../services/selectors/allSelectors";
import { SearchBar } from "./searchBar";

import { handleLogoutPharmacy } from "../../services/actions/main/logout";
import { removeWorkSpaceTab } from "../../utils/helper/helper";
import { CandaTimeZone } from "./timeZone";
import { changelog_today_count } from "../../networking/urlEndPoints";

import {
	getFallbackUserImage,
	normalizeAssetUrl,
	resolveExternalBaseUrl
} from "../../utils/helper/assetUrl";

import ResponsiveSidebar from "./responsiveSidebar";
import LabelSetting from '../pharmacy/dashboard/labeltoMargin'
import ConsentToEmail from '../pharmacy/dashboard/consenttoEmail'
import UpdateAuthenticationNumber from "../pharmacy/dashboard/updateAuthenticationNumber";
import {
	formatUnreadBadge,
	openPatientChat,
	usePatientChatUnreadCounts
} from "../patient-chat/PatientChatLauncher";
import DeliveryAppDownloadModal from "../pharmacy/delivery/DeliveryAppDownloadModal";
import Axios from "../../networking/intraceptor";

import { filterNavItems, navItemAllowed } from "../../constants/moduleSubItems";
import useMyModules, { buildModuleGate } from "../../hooks/useMyModules";

import * as constants from "../../constants/index";
import * as Hooks from "../../hooks";
import * as useEffectHelper from "../../utils/helper/useEffectsHelper";
import * as Icons from "../../assets/svg/icons";
import * as routeNames from "../../routes/routeName";

const Header = () => {

	const dropdownRef = useRef(null);
	const navigate = Hooks.useCustomNavigate();
	const location = Hooks.useCustomLocation();
	const { authData, pharmacyLoginData, adminModule, rawAdminModule, profileData, fileManagerUrl } = useCustomSelectors();

	const [isDropdownOpen, setDropdownOpen] = Hooks.useCustomState(false);
	const [tooltipVisible, setTooltipVisible] = Hooks.useCustomState(null);
	const [loading, setLoading] = Hooks.useCustomState(false);
	const [isModalOpen, setModalOpen] = Hooks.useCustomState(false);
	const [isDeliveryAppModalOpen, setDeliveryAppModalOpen] = Hooks.useCustomState(false);
	const [copiedLink, setCopiedLink] = useState('Copy Attendance Link')
	const [changelogCount, setChangelogCount] = useState(0);
	const { totalUnreadCount } = usePatientChatUnreadCounts();
	const patientChatBadge = formatUnreadBadge(totalUnreadCount);
	const showPatientChatIcon = pharmacyLoginData?.app_mode === 1;
	const getSafeImageSrc = useCallback((src) => normalizeAssetUrl(src) || getFallbackUserImage(), []);
	const attendanceBaseUrl = resolveExternalBaseUrl(process.env.REACT_APP_emplyoee_attendace_link);

	useEffect(() => {
		initFlowbite();
	}, []);

	// Fetch changelog count function
	const fetchChangelogCount = useCallback(async () => {
		try {
			const response = await Axios.get(changelog_today_count);
			if (response?.data?.data?.count !== undefined) {
				setChangelogCount(response.data.data.count);
			}
		} catch (error) {
			console.error("Error fetching changelog count:", error);
		}
	}, []);

	// Fetch count on mount
	useEffect(() => {
		fetchChangelogCount();
	}, [fetchChangelogCount]);

	// Refetch count when route changes
	useEffect(() => {
		// When navigating to changelog page, refetch after a short delay
		// to allow backend to process any "mark as read" actions
		if (location.pathname === routeNames.CHANGE_LOG_ROUTE) {
			const timer = setTimeout(() => {
				setChangelogCount(0);
			}, 500); // Small delay to allow backend processing
			return () => clearTimeout(timer);
		}
	}, [location.pathname]);

	useEffectHelper.useHeaderEffect(setDropdownOpen, dropdownRef);

	// THIRD nav surface: the avatar menu. It used to answer only to
	// adminModule, so "Team" showed to an admin who no longer held the TEAM
	// module — a link the route guard then bounced. Same gate, same helpers as
	// workspaceTabs.js and sidebar.js (deliberately NOT a second rule set), so
	// the three surfaces cannot drift. FAIL-OPEN: in legacy mode
	// (modules === null) navItemAllowed passes everything, i.e. today's menu.
	const { hasKey } = buildModuleGate(useMyModules());
	const moduleAllowsItem = (route) => navItemAllowed(route, hasKey);

	const HeaderdropDownItems = filterNavItems(
		constants.HeaderdropDownItems(
			navigate,
			setDropdownOpen,
			setModalOpen,
			isModalOpen,
			adminModule
		),
		hasKey
	);

	const handleCopy = (link) => {
		const tempInput = document.createElement('input');
		tempInput.value = link;
		document.body.appendChild(tempInput);
		tempInput.select();
		document.execCommand('copy');
		document.body.removeChild(tempInput)

		setCopiedLink('Copied')
		setTimeout(() => {
			setCopiedLink('Copy Attendance Link')
		}, 3000)
	}

	return (
		<>

			<div className="container-fluid mx-auto px-4 pt-5 pb-5 flex items-center flex-wrap gap-y-2 ">
				<SearchBar />
				{/* <div className="flex flex-wrap xl:ml-auto items-center gap-x-4   lg:gap-x-2 gap-y-2 "> */}
				<div className="flex flex-wrap xl:ml-auto items-center gap-1 ">
					<CandaTimeZone />
					{showPatientChatIcon && (
						<button
							type="button"
							className="md:flex hidden  relative w-8 h-10 rounded-md items-center justify-center text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
							onClick={() => openPatientChat(null, navigate)}
							onMouseEnter={() => setTooltipVisible(13)}
							onMouseLeave={() => setTooltipVisible(null)}
							aria-label="Open patient chat"
						>
							<MessageCircle size={24} strokeWidth={2.2} />
							{patientChatBadge && (
								<span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center border border-white">
									{patientChatBadge}
								</span>
							)}
							{tooltipVisible === 13 && (
								<div style={{ background: 'rgb(170 180 225 / 72%)', border: '1px solid white' }}
									className="whitespace-nowrap py-1 items-center justify-center mt-10 absolute top-0 left-0 px-4 fs-12 z-10 visible text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-lg opacity-100 tooltip dark:bg-gray-700">
									Patient Chat
								</div>
							)}
						</button>
					)}
					<button className=" md:flex hidden mr-1 relative"
						onClick={() => { navigate(routeNames.CHANGE_LOG_ROUTE); removeWorkSpaceTab(); }}
						onMouseEnter={() => setTooltipVisible(11)}
						onMouseLeave={() => setTooltipVisible(null)}>
						<Icons.HeaderChangeLogIcon />
						{changelogCount > 0 && (
							<span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center">
								{changelogCount}
							</span>
						)}
						{tooltipVisible === 11 && (
							<div style={{ background: 'rgb(170 180 225 / 72%)', border: '1px solid white' }}
								className="whitespace-nowrap py-1 items-center justify-center mt-8 absolute top-0 left-0 px-4 fs-12  z-10 visible  
												text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-lg opacity-100 tooltip 
												dark:bg-gray-700">
								Change Log
							</div>
						)}
					</button>

					<button
						className="md:flex hidden mr-1 relative"
						type="button"
						onClick={() => setDeliveryAppModalOpen(true)}
						onMouseEnter={() => setTooltipVisible(12)}
						onMouseLeave={() => setTooltipVisible(null)}
						aria-label="Open delivery app download QR code"
					>
						<QrCodeIcon className="h-7 w-7 text-white" strokeWidth={2.2} />
						{tooltipVisible === 12 && (
							<div style={{ background: 'rgb(170 180 225 / 72%)', border: '1px solid white' }}
								className="whitespace-nowrap py-1 items-center justify-center mt-8 absolute top-0 left-0 px-4 fs-12 z-10 visible
												text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-lg opacity-100 tooltip
												dark:bg-gray-700">
								Delivery App
							</div>
						)}
					</button>

					{pharmacyLoginData?.sms_status === 1 && (
						<button className=" md:flex hidden mr-1 relative">
							<a href="/pharmacy-sms?active=text-messages" target="_blank" rel="noreferrer" onMouseEnter={() => setTooltipVisible(10)}
								onMouseLeave={() => setTooltipVisible(null)}>
								<Icons.HeaderMessageIcon />
							</a>

							{tooltipVisible === 10 && (
								<div style={{ background: 'rgb(170 180 225 / 72%)', border: '1px solid white' }}
									className="whitespace-nowrap py-1 items-center justify-center mt-8 absolute top-0 left-0 px-4 fs-12  z-10 visible  
											text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-lg opacity-100 tooltip 
											dark:bg-gray-700">
									Messages
								</div>
							)}
						</button>
					)}

					{fileManagerUrl &&
						<button className="md:flex hidden  mr-1 relative">

							<a href={fileManagerUrl} target="_blank" rel="noreferrer" onMouseEnter={() => setTooltipVisible(9)}
								onMouseLeave={() => setTooltipVisible(null)}>
								<Icons.HeaderFileManagerIcon />
							</a>

							{tooltipVisible === 9 && (
								<div style={{ background: 'rgb(170 180 225 / 72%)', border: '1px solid white' }}
									className="whitespace-nowrap py-1 items-center justify-center mt-8 absolute top-0 left-0 px-4 fs-12  z-10 visible  
												text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-lg opacity-100 tooltip 
												dark:bg-gray-700">
									File Manager
								</div>
							)}
						</button>}

					<button className="md:flex hidden  relative">
						<div
							onMouseEnter={() => setTooltipVisible(8)}
							onMouseLeave={() => setTooltipVisible(null)}
							onClick={() => handleCopy(`${attendanceBaseUrl}${pharmacyLoginData?.slug}/tm`)}
						>
							<Icons.AttendanceIcon className="size-8 btnnn text-[#4A5899]" />
						</div>

						{tooltipVisible === 8 && (
							<div style={{ background: 'rgb(170 180 225 / 72%)', border: '1px solid white' }}
								className="whitespace-nowrap py-1 items-center justify-center mt-8 absolute top-0 left-0 px-4 fs-12  z-10 visible  
												text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-lg opacity-100 tooltip 
												dark:bg-gray-700">
								{copiedLink}
							</div>
						)}
					</button>

					{constants?.createNavigationButtonsWithTooltips?.map(
						(value, index) => (
							<React.Fragment key={index}>
								{!moduleAllowsItem(value.route) ? null :
									/* INVERTED CHECK — it hides these three icons FROM admins, who
									   reach them through TEAM instead. It therefore reads the user's
									   REAL admin flag: with the effective one, screen privacy (F9 x5)
									   would drop it to 0 and ADD three icons to the header, when
									   hiding must only ever remove. */
									rawAdminModule === 1 && (value.tooltipsValue === 'Time Off Request' || value.tooltipsValue === 'Tasks Assignments' || value.tooltipsValue === 'Appreciations') ? null :
									<button
										onMouseEnter={() => setTooltipVisible(index)}
										onMouseLeave={() => setTooltipVisible(null)}
										className=" md:flex hidden  py-3 px-1 border-none rounded-full lg:rounded-none  items-center gap-4 relative"
									>
										<div
											onClick={() => {
												navigate(value.route);
												removeWorkSpaceTab();
											}}
										>
											{value.icon}
										</div>
										{tooltipVisible === index && (
											<div
												style={{ background: 'rgb(170 180 225 / 72%)', border: '1px solid white' }}
												onClick={() => {
													navigate(value.route);
													removeWorkSpaceTab();
												}}
												className="whitespace-nowrap py-1 items-center justify-center mt-12 absolute top-0 left-0 px-4 fs-12  z-10 visible  
														text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-lg opacity-100 tooltip 
														dark:bg-gray-700">
												{value.tooltipsValue}
											</div>
										)}
									</button>}
							</React.Fragment>
						)
					)}

					<div className="md:flex hidden items-center  whitespace-nowrap appearance-none min-w-34  text-white py-2 px-2 border-none  rounded lg:border bg-[#4E64C2]">
						<div className="relative">
							<button
								className=" flex  items-center text-sm font-medium text-white rounded-full md:mr-0 dark:text-white"
								type="button"
								onClick={() => setDropdownOpen(!isDropdownOpen)}
							>
								<span className="sr-only">Open user menu</span>
								<div className="relative inline-flex items-center justify-center w-8 h-8 mr-2 overflow-hidden">
									<span className="light-color rubik-500 fs-22">
										{profileData?.photo ?
											<img
												src={getSafeImageSrc(profileData?.photo)}
												alt=""
												className="rounded-sm w-full h-full"
												onError={(e) => {
													e.currentTarget.src = getFallbackUserImage();
												}}
											/>
											: <img
												src={getSafeImageSrc(authData?.data?.pharmacy[0]?.logo)}
												alt=""
												className="rounded-sm"
												onError={(e) => {
													e.currentTarget.src = getFallbackUserImage();
												}}
											/>
										}
										{/* <img src={authData?.data?.pharmacy[0]?.logo} alt="" className="rounded-sm" /> */}
										{/* <img src={profileData?.photo} alt="" className="rounded-sm w-full h-full" /> */}
									</span>
								</div>
								{/* {authData?.data?.pharmacyname} */}
								{profileData?.name}
								<Icons.ChevronDownIcon />
							</button>

							{isDropdownOpen && (
								<div
									ref={dropdownRef}
									className={`z-50 absolute  ${isDropdownOpen ? "block" : "hidden"
										} ot_headerDropdown`}
								>
									{authData?.data?.pharmacy?.map((value, index) => {
										return (
											<div
												className="flex items-center gap-2 px-5 py-2 pb-3"
												key={index}
											>
												<div className="relative inline-flex items-center justify-center min-w-12 h-12 overflow-hidden">
													{/* <span className="light-color rubik-500 fs-24"> */}
													{/* {profileData?.photo ?
																	<img src={profileData?.photo} className="" />
																	: <img src={value?.logo} className="" />} */}
													<img
														src={getSafeImageSrc(value?.logo)}
														className="w-12 h-12"
														alt=""
														onError={(e) => {
															e.currentTarget.src = getFallbackUserImage();
														}}
													/>
													{/* </span> */}
												</div>
												<div>
													<h3 className="dark-color rubik-500 fs-14">
														{value.name}
													</h3>
													<p className="dark-color rubik-400 fs-16">
														{value.city}
													</p>
												</div>
											</div>
										);
									})}

									<ul
										className="py-2 text-sm text-gray-700 dark:text-gray-200"
										aria-labelledby="dropdownInformdropdownAvatarNameButtonationButton"
									>
										<LabelSetting />
										<ConsentToEmail />
										{adminModule === 1 && <UpdateAuthenticationNumber />}
										{HeaderdropDownItems?.map((item, index) => (
											<li
												key={index}
												onClick={item.onClick}
												className="btnnn"
											>
												<span className="flex gap-4 items-center px-5 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white grey-color rubik-400 fs-16">
													{item.icon}
													{item.label}
												</span>
											</li>
										))}

									</ul>
								</div>
							)}
						</div>

						<button
							style={{ width: "32px", height: "32px" }}
							className=" sm:block md:block lg:hidden ml-1 flex items-center text-sm font-medium text-white rounded-full md:mr-0 dark:text-white"
							type="button"
							onClick={() => setDropdownOpen(!isDropdownOpen)}
						>
							<span className="sr-only">Open user menu</span>
							<div className="relative inline-flex items-center justify-center  overflow-hidden  rounded-full">
								<img
									src={getSafeImageSrc(authData?.data?.pharmacy[0]?.logo)}
									alt=""
									onError={(e) => {
										e.currentTarget.src = getFallbackUserImage();
									}}
								/>
							</div>
						</button>
					</div>
					<button
						className="hidden  text-white px-3 py-3 mr-1 rounded-full bg-white bg-opacity-10 bg-transparent h-12  items-center"
						type="button"
						data-drawer-target="drawer-navigation"
						data-drawer-show="drawer-navigation"
						aria-controls="drawer-navigation"
						data-drawer-backdrop="false"
					>
						<Icons.HeaderMenuIcon />
					</button>
				</div>
				<ResponsiveSidebar />
			</div>


			{isModalOpen && (
				<div style={{ zIndex: '9999999' }} className="fixed top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center 
														p-6 overflow-x-hidden overflow-y-auto bg-black bg-opacity-50 dark:bg-opacity-100">
					<div className="relative w-full max-w-md max-h-full">
						<div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
							<button onClick={() => setModalOpen(false)}
								type="button" className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 
								hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
								<Icons.ModalCloseIcon />
								<span className="sr-only">Close modal</span>
							</button>
							<div className="p-6 text-center">
								<Icons.WarningIcon />
								<h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
									Are you sure you want to logout ?
								</h3>
								<button
									onClick={() => handleLogoutPharmacy(setLoading, navigate)}
									style={{ backgroundColor: "#3A52BB" }}
									type="button"
									className="text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 dark:focus:ring-red-800 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center mr-2"
								>
									{loading ? "Processing..." : "Yes, I'm sure "}
								</button>
								<button
									onClick={() => setModalOpen(false)}
									data-modal-hide="popup-modal"
									type="button"
									className="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600"
								>
									No, cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			<DeliveryAppDownloadModal
				isOpen={isDeliveryAppModalOpen}
				onClose={() => setDeliveryAppModalOpen(false)}
			/>
		</>
	);
};

export default Header;
